import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    Typography,
    TextField,
    Button,
    Box,
    CircularProgress
} from '@mui/material';

export default function VerificationModal({ 
    open, 
    onClose, 
    onVerify, 
    onResendCode,
    phoneNumber, 
    loading,
    verificationError
}) {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [resendLoading, setResendLoading] = useState(false);

    // Clear local error and success message when modal opens/closes
    useEffect(() => {
        if (open) {
            setCode('');
            setError('');
            setSuccessMessage('');
        }
    }, [open]);

    // Handle verification error from parent
    useEffect(() => {
        if (verificationError) {
            setError('Invalid verification code. Please try again.');
            setSuccessMessage('');
        }
    }, [verificationError]);

    const handleResend = async () => {
        setResendLoading(true);
        try {
            await onResendCode();
            setError('');
            setSuccessMessage('A new verification code has been sent to your phone.');
            setCode('');
        } catch (err) {
            setError('Failed to send new code. Please try again later.');
            setSuccessMessage('');
        } finally {
            setResendLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (code.length !== 6) {
            setError('Please enter a 6-digit verification code');
            setSuccessMessage('');
            return;
        }
        setError('');
        setSuccessMessage('');
        onVerify(code);
    };

    const handleCodeChange = (e) => {
        const value = e.target.value.replace(/\D/g, '');
        if (value.length <= 6) {
            setCode(value);
            setError('');
            setSuccessMessage('');
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2, p: 2 }
            }}
        >
            <DialogContent sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: 3,
                py: 4
            }}>
                <Typography variant="h5" align="center" gutterBottom>
                    Verify Your Phone Number
                </Typography>
                
                <Typography variant="body1" align="center" color="text.secondary">
                    We've sent a 6-digit verification code to:
                </Typography>
                
                <Typography variant="h6" align="center" sx={{ fontWeight: 'bold' }}>
                    {phoneNumber}
                </Typography>
                
                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            fullWidth
                            label="Verification Code"
                            value={code}
                            onChange={handleCodeChange}
                            placeholder="123456"
                            inputProps={{
                                maxLength: 6,
                                style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
                            }}
                            error={!!error}
                            helperText={error || successMessage}
                            FormHelperTextProps={{
                                sx: {
                                    color: successMessage ? 'success.main' : 'error.main'
                                }
                            }}
                            disabled={loading || resendLoading}
                        />
                        
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading || resendLoading || code.length !== 6}
                            sx={{ 
                                mt: 2,
                                py: 1.5,
                                bgcolor: '#2B0063',
                                '&:hover': {
                                    bgcolor: '#1a003d'
                                }
                            }}
                        >
                            {loading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={20} color="inherit" />
                                    Verifying...
                                </Box>
                            ) : (
                                'Verify Code'
                            )}
                        </Button>

                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleResend}
                            disabled={loading || resendLoading}
                            sx={{ 
                                py: 1.5,
                                borderColor: '#2B0063',
                                color: '#2B0063',
                                '&:hover': {
                                    borderColor: '#1a003d',
                                    color: '#1a003d',
                                    backgroundColor: 'rgba(43, 0, 99, 0.04)'
                                }
                            }}
                        >
                            {resendLoading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={20} color="inherit" />
                                    Sending...
                                </Box>
                            ) : (
                                'Resend Code'
                            )}
                        </Button>

                    </Box>
                </form>
                
                <Typography variant="body2" color="text.secondary" align="center">
                    Didn't receive a code? Click the resend button above.
                </Typography>
            </DialogContent>
        </Dialog>
    );
}